import * as RadixTooltip from '@radix-ui/react-tooltip';
import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react';

import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { OverlayBody } from './OverlayBody';
import {
  OVERLAY_DESC_CLASS,
  OVERLAY_MAX_WIDTH_CLASS,
  OVERLAY_SURFACE,
  surfaceRadius,
  surfaceShape,
} from './surface';
import { radixSideOffset, TIP_ARROW_SIZE, TipArrow } from './TipArrow';
import type { TooltipProps } from './Tooltip.types';
import { HasTooltipProviderContext } from './TooltipProvider.context';

/**
 * Cross-platform Tooltip — web implementation.
 *
 * Thin wrapper over `@radix-ui/react-tooltip` so we inherit the heavy
 * lifting (collision-aware positioning + side-flipping, focus management,
 * keyboard navigation, enter/exit animations, portal). The native
 * counterpart at `Tooltip.native.tsx` is a custom impl since Radix is
 * web-only.
 *
 * Trigger requirements: the child element must forward refs (Radix's
 * `Trigger asChild` clones the child and binds a ref to it). The shared
 * `Pressable` does this — so any cross-platform component built on top
 * of it (Button, etc.) just works. If you pass a non-forwardRef function
 * component, Radix logs a console warning and tooltips may be misplaced.
 */
export function Tooltip({
  children,
  title,
  media,
  content,
  keybinding,
  side = 'top',
  delayMs = 400,
  defaultOpen,
  open,
  onOpenChange,
  disabled,
  className,
  align = 'center',
  sideOffset = 8,
  testID,
}: TooltipProps) {
  // Read before the early returns — hooks can't run conditionally.
  const hasProvider = useContext(HasTooltipProviderContext);

  // Skip the whole Radix tree when nothing would show. Also skips when
  // there's nothing to render as a trigger.
  if (
    disabled ||
    (content == null && title == null && media == null) ||
    content === false
  ) {
    return <>{children}</>;
  }

  const child = Children.only(children);
  if (!isValidElement(child)) {
    return <>{children}</>;
  }

  // Rich (title / media card) vs Simple (single-line hint).
  const rich = title != null || media != null;

  return (
    <MaybeProvider hasProvider={hasProvider} delayMs={delayMs}>
      <RadixTooltip.Root
        // Per-instance delay — overrides the provider's fallback. Radix's
        // skip-delay (hover A → hover B opens instantly) is provider state,
        // so it only works when an app-level `TooltipProvider` is mounted.
        delayDuration={delayMs}
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={onOpenChange}>
        <RadixTooltip.Trigger asChild>{child as ReactElement}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            data-testid={subTestID(testID, 'content')}
            side={side}
            align={align}
            // `sideOffset` is the trigger→box gap; Radix would add the arrow
            // on top of it — see `radixSideOffset`.
            sideOffset={radixSideOffset(sideOffset)}
            collisionPadding={12}
            // Keeps the Tip Arrow off the rounded corners — see `surfaceRadius`.
            arrowPadding={surfaceRadius(rich)}
            className={cn(
              // Figma "Tooltip" set (Simple node 2205:13006 / Rich 2205:12922):
              // translucent secondary-100 surface, secondary-200 border, the
              // 0/2/8 @ 10% drop shadow, and per-form radius/padding from
              // `surfaceShape`. All colors are CSS-var palette tokens so they
              // auto-invert in dark mode — no `dark:` classes. Z-index is high
              // enough to sit on top of dialogs and popovers.
              // A Tooltip's content is text-only by contract (no actions —
              // that's Popover), so it must never eat the pointer: a box
              // floating over its neighbours otherwise blocks THEIR hover, and
              // grids of small triggers (the heatmap's day cells) stop working
              // — you hover one cell and the rest of the column goes dead.
              // Radix's own positioning wrapper is the one that intercepts, and
              // it takes no className, so reach it from here via `:has()`.
              'pointer-events-none ' +
                '[[data-radix-popper-content-wrapper]:has(>&)]:pointer-events-none ' +
                'z-[1000] text-pretty break-words outline-none ' +
                'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)] ' +
                'animate-in fade-in-0 zoom-in-95 ' +
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ' +
                'data-[state=closed]:zoom-out-95 ' +
                'data-[side=bottom]:slide-in-from-top-2 ' +
                'data-[side=left]:slide-in-from-right-2 ' +
                'data-[side=right]:slide-in-from-left-2 ' +
                'data-[side=top]:slide-in-from-bottom-2',
              OVERLAY_SURFACE,
              // Enforced here, not per call site: a JSX `content` would
              // otherwise inherit the page's font size (see OVERLAY_DESC_CLASS).
              OVERLAY_DESC_CLASS,
              surfaceShape(rich),
              className,
              // AFTER `className` on purpose — a hint is never wide enough to
              // justify opting out, and call sites had accumulated their own
              // `max-w-*` (up to 30rem) that read as inconsistent tooltip
              // widths across the app.
              OVERLAY_MAX_WIDTH_CLASS,
            )}>
            {/* Header (title) + media + description (+ keybind keycaps). A
                Tooltip has no actions, so `content` stays text-only. */}
            <OverlayBody title={title} media={media} keybinding={keybinding}>
              {content}
            </OverlayBody>
            {/* Figma "Tip Arrow" — see TipArrow for the seamless-base geometry. */}
            <RadixTooltip.Arrow asChild {...TIP_ARROW_SIZE}>
              <TipArrow />
            </RadixTooltip.Arrow>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </MaybeProvider>
  );
}

/**
 * Fallback provider for trees with no app-level {@link TooltipProvider}
 * (Storybook stories, jest tests, anything that forgot to mount it): Radix
 * THROWS when `Tooltip.Root` has no provider above it. Such a tooltip is an
 * island — it can't share skip-delay state — which is exactly why the app root
 * should mount the real provider.
 */
function MaybeProvider({
  hasProvider,
  delayMs,
  children,
}: {
  hasProvider: boolean;
  delayMs: number;
  children: ReactNode;
}) {
  if (hasProvider) return <>{children}</>;
  return (
    <RadixTooltip.Provider delayDuration={delayMs}>{children}</RadixTooltip.Provider>
  );
}
